/**
 * MF2 runtime security plugin for the abax-ui remote.
 *
 * Re-homes the host's remote-loading security onto @module-federation/runtime
 * hooks, upgraded to a *signed* trust root. The bespoke unsigned manifest is
 * replaced by MF2's `mf-manifest.json` plus a detached JWS (ES256) whose public
 * key is baked into THIS host bundle, outside the @abax/ui folder it verifies
 * (abax-ui-manifest-public-key.ts says where that anchor stops holding). Only
 * after the signature verifies are the manifest's provenance
 * claims (commit pin, remoteEntry SRI) trusted. This closes the circular-trust
 * flaw of the previous scheme, where a manifest tamper rewrote the very pin and
 * hash it was checked against.
 *
 * Scope of the guarantee: the signed root proves the manifest and the
 * remoteEntry.js are AUTHENTIC (right key) and INTEGRAL (right bytes). Every
 * script the host itself injects through the `createScript` hook — the
 * remoteEntry AND any expose asset the runtime preloads through that same hook —
 * carries per-file SRI resolved by filename from the signed
 * `metaData.chunkIntegrity` map, so a CDN rewrite of any of those bytes is
 * rejected by the browser. A single global SRI (the remoteEntry hash) must NOT
 * be applied to a preloaded chunk: the hashes differ, and the browser then
 * rejects the correct bytes as an integrity failure. This does NOT reach chunks
 * the remote loads through its OWN webpack JSONP (the host cannot intercept
 * those), and it does NOT prove FRESHNESS on its own (an older validly-signed
 * manifest still verifies). Freshness is guarded best-effort by the client
 * monotonic floor below.
 *
 * Hook map (each preserves an invariant of the previous hand-rolled loader):
 *  - `fetch`             : manifest-URL allowlist (throw); fetch the bytes + the
 *                          `.jws` sidecar with a bounded transient retry; verify
 *                          the detached JWS; latch the verified URL; enforce the
 *                          monotonic freshness floor; return a Response over the
 *                          *verified* bytes. Missing/invalid signature =
 *                          ManifestTamperError (fail-closed). [I1, I9, I10, I11]
 *  - `loadRemoteSnapshot`: the manifest is now signature-verified — confirm it
 *                          came through the verified-manifest latch, read
 *                          `metaData.provenance`: commit pin (throw on mismatch /
 *                          fail-closed on missing pin), stash the remoteEntry
 *                          SRI and the per-file `chunkIntegrity` map for
 *                          `createScript`. [I2, I4, I5, I6]
 *  - `createScript`      : final-URL allowlist (throw); return a <script> with
 *                          per-file `integrity` (looked up by filename in the
 *                          signed chunkIntegrity map; remoteEntry falls back to
 *                          the stashed entry SRI and is mandatory when pinned) +
 *                          `crossOrigin` + `referrerPolicy`. [I3, I7, I8]
 *  - `errorLoadRemote`   : the fork — a ManifestTamperError (tamper / pin /
 *                          signature) returns undefined so the runtime re-throws
 *                          (NO fallback; preserves CWE-829 fail-closed); a
 *                          transient error signals degraded mode and re-throws so
 *                          the host's `__ABAX_UI_LOAD_FAILED__` path renders the
 *                          consumer fallback. [I9, I10, I12]
 *
 * The signature verify is the one genuinely new gate; everything else is the
 * existing `manifest.ts` / `validate-url.ts` behavior expressed as MF2 hooks.
 */

import { compactVerify, importSPKI, base64url } from 'jose';
import { rehomeCdnUrlToDocumentOrigin } from '@abax/platform';
import { ABAX_UI } from '@/config/federation-package-ids';
import { validateRemoteUrl } from './validate-url';
import { ManifestHttpError, ManifestTamperError } from './manifest';
import { readPipelineFloor, recordPipelineFloor } from './manifest-freshness';

/** The key type `importSPKI` yields (a WebCrypto CryptoKey in the browser). */
type ManifestVerifyKey = Awaited<ReturnType<typeof importSPKI>>;

/** Re-exported so the previous `INTEGRITY_RE` guard travels with the SRI value. */
const INTEGRITY_RE = /^sha(256|384|512)-[A-Za-z0-9+/]+={0,2}$/;

/**
 * A single CDN path segment (the deploy IID) — no slashes, no traversal. Must
 * start with an alphanumeric so dot-only segments (`.`, `..`) and leading-dot
 * names can never derive a parent-directory publicPath, even though the IID rides
 * the verified signature (defense-in-depth; the producer only ever emits a
 * numeric CI IID).
 */
const SAFE_SEGMENT_RE = /^[A-Za-z0-9][A-Za-z0-9._-]*$/;

/**
 * Absolute `publicPath` for the versioned, immutable assets, derived from the
 * (signed) deploy IID and the manifest URL. MF2's `getResourceUrl` concatenates
 * `publicPath + filename`, so this MUST be an absolute URL: an `auto` publicPath
 * resolves to the manifest's own directory, which is wrong when the manifest is
 * served from a STABLE (non-versioned) URL. Idempotent — when the manifest is
 * already served from the versioned directory (dev/co-located), `auto` is
 * already correct and this returns that same directory.
 */
function deriveVersionedPublicPath(manifestUrl: string, iid: string): string {
  const manifestDir = new URL('./', manifestUrl).href;
  return manifestDir.endsWith(`/${iid}/`) ? manifestDir : new URL(`${iid}/`, manifestUrl).href;
}

/**
 * Last path segment of a script URL (its filename), used to look up the
 * script's own hash in the signed chunkIntegrity map. Query/hash are stripped by
 * URL parsing. Returns undefined on an unparseable URL — the allowlist has
 * already validated it, so this is belt-and-suspenders.
 */
function filenameFromScriptUrl(url: string): string | undefined {
  try {
    const { pathname } = new URL(url);
    const seg = pathname.slice(pathname.lastIndexOf('/') + 1);
    return seg || undefined;
  } catch {
    return undefined;
  }
}

/**
 * The host instance also loads parcels, and every hook fires for them too. Only
 * abax-ui carries the signed manifest this plugin trusts, so a hook handed any
 * other remote leaves it to the runtime default. A hook with no remote name is
 * still treated as abax-ui, so a payload that drops the name cannot skip the gates.
 */
const isOtherRemote = (name: string | undefined): boolean => !!name && name !== ABAX_UI;

/**
 * `errorLoadRemote` carries no `remote` on the `beforeRequest` lifecycle, nor on `onLoad`
 * when the id matched no registered remote. Remotes on this instance are registered by
 * name only, so the request id (`<name>/<expose>`) still names the remote.
 */
const remoteNameFromRequestId = (id: string | undefined): string | undefined =>
  id?.split('/')[0] || undefined;

/** Bounded retry for transient manifest/sidecar fetches (CDN blip resilience). */
const FETCH_RETRIES = 2;
const RETRY_BACKOFF_MS = 200;
const FETCH_TIMEOUT_MS = 2500;

export interface AbaxUiSecurityPluginOptions {
  /**
   * SPKI PEM of a single manifest-signing public key, baked into the host build.
   * Convenience for a one-key environment; `publicKeyPems` supersedes it. This is
   * an out-of-band trust anchor — it MUST NOT be fetched from the CDN that serves
   * the manifest, or the circular trust returns.
   */
  publicKeyPem?: string;
  /**
   * One or more SPKI PEMs, any of which verifies the manifest. Used for the
   * environment-scoped trust set (e.g. staging trusts the staging + production
   * keys) so a build signed by any trusted key loads. A PEM that fails to import
   * (an un-provisioned placeholder) is skipped, not fatal, as long as another
   * trusted key verifies. Takes precedence over `publicKeyPem` when non-empty.
   */
  publicKeyPems?: string[];
  /** Expected source commit SHA. When set, the signed provenance must match. */
  expectedCommit?: string;
  /**
   * When true, a missing `expectedCommit` is a hard failure (production). A
   * forgotten pin then fails closed rather than silently shipping unpinned.
   */
  requireCommitPin?: boolean;
  /**
   * Neither check nor record the freshness floor. For a merge request preview,
   * whose pipeline IID is usually above the stable build's: recording it would
   * make the client refuse the stable build once the preview ends.
   */
  skipFreshnessFloor?: boolean;
  /**
   * Manifest + sidecar fetch timeout (ms). Operator-tunable via env-config
   * (`ABAX_UI_MANIFEST_TIMEOUT_MS`); falls back to the baked-in default when
   * unset or non-positive.
   */
  timeoutMs?: number;
  /** Called once when a load degrades (transient or tamper), for the flag/log. */
  onDegraded?: (reason: 'tamper' | 'transient', detail: string) => void;
}

/** SLSA-shaped provenance the producer writes into `metaData.provenance`. */
interface ManifestProvenance {
  predicateType?: string;
  subject?: Array<{ name?: string; digest?: { sha384?: string } }>;
  buildDefinition?: {
    resolvedDependencies?: Array<{ digest?: { gitCommit?: string } }>;
  };
}

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Fetch with a bounded transient retry. A non-ok HTTP status or a network
 * error is retried up to `FETCH_RETRIES` times; the final failure is thrown as
 * a `ManifestHttpError` (transient class → the fork degrades, never a tamper).
 */
async function fetchWithRetry(
  url: string,
  timeoutMs: number = FETCH_TIMEOUT_MS,
): Promise<Uint8Array> {
  let lastStatus = 0;
  for (let attempt = 0; attempt <= FETCH_RETRIES; attempt += 1) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await fetch(url, {
        cache: 'no-cache',
        credentials: 'omit',
        signal: controller.signal,
        // Fail closed on redirects: the allowlist validates the configured URL,
        // not a post-redirect Location. Following a 3xx from an allowlisted
        // origin to an attacker host would load bytes the allowlist never saw.
        redirect: 'error',
      });
      if (res.ok) {
        return new Uint8Array(await res.arrayBuffer());
      }
      lastStatus = res.status;
      // 4xx is not transient (path moved / ACL drift) — do not waste retries.
      if (res.status >= 400 && res.status < 500) {
        throw new ManifestHttpError(res.status, url);
      }
    } catch (err) {
      if (err instanceof ManifestHttpError) throw err;
      // network / abort — retry unless exhausted
      if (attempt === FETCH_RETRIES) {
        throw new ManifestHttpError(lastStatus || 0, url);
      }
    } finally {
      clearTimeout(timer);
    }
    if (attempt < FETCH_RETRIES) await sleep(RETRY_BACKOFF_MS * (attempt + 1));
  }
  throw new ManifestHttpError(lastStatus || 0, url);
}

/**
 * Verify a detached compact JWS (ES256) over the exact manifest bytes.
 *
 * Producer format (see packages/ui/scripts/sign-manifest.mjs): the compact JWS
 * with its base64url payload segment removed — `BASE64URL(header)..BASE64URL(sig)`.
 * The host reconstructs the signing input by re-inserting the base64url of the
 * exact served bytes, so the signature covers precisely what was fetched (no
 * re-serialization ambiguity).
 *
 * Throws on any verification failure — the caller converts that to a
 * ManifestTamperError (fail-closed).
 */
async function verifyDetachedManifest(
  manifestBytes: Uint8Array,
  detachedJws: string,
  publicKey: ManifestVerifyKey,
): Promise<void> {
  const segments = detachedJws.split('.');
  if (segments.length !== 3 || segments[1] !== '') {
    throw new Error('detached JWS must be of the form "<header>..<signature>"');
  }
  const reconstructed = `${segments[0]}.${base64url.encode(manifestBytes)}.${segments[2]}`;
  // compactVerify enforces alg via the key's algorithm; ES256 only.
  await compactVerify(reconstructed, publicKey, { algorithms: ['ES256'] });
}

/**
 * Build the abax-ui MF2 security runtime plugin. Returns a plain plugin object
 * for `createInstance({ plugins: [...] })`. Typed loosely against the runtime
 * hook payloads (the 2.6.0 shapes) to avoid coupling to internal type paths;
 * the security logic, not the types, is the contract.
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types -- returns a deliberately MF2-decoupled plugin object; call site casts to ModuleFederationRuntimePlugin.
export function createAbaxUiSecurityPlugin(options: AbaxUiSecurityPluginOptions) {
  const {
    publicKeyPem,
    publicKeyPems,
    expectedCommit,
    requireCommitPin,
    skipFreshnessFloor,
    timeoutMs,
    onDegraded,
  } = options;

  // Normalize to the trusted-key list. `publicKeyPems` wins when non-empty;
  // otherwise fall back to the single `publicKeyPem`. Empty is allowed at
  // construction — verification simply finds no key and fails closed.
  const trustedPems =
    publicKeyPems && publicKeyPems.length > 0 ? publicKeyPems : publicKeyPem ? [publicKeyPem] : [];

  // "Pinned" = a production/staging posture where provenance is enforced. In
  // pinned mode the remoteEntry SRI is MANDATORY: the JWS binds the manifest
  // bytes, not the entry, so the `<script integrity>` is the only thing binding
  // the served remoteEntry.js against a CDN rewrite.
  const pinned = Boolean(expectedCommit || requireCommitPin);

  // Setting an explicit commit pin is a DELIBERATE freeze (or rollback) to one
  // build, so the forward-only freshness floor is bypassed then: the operator
  // has named the exact commit and that intent wins over "never go backwards".
  const floorEnabled = !expectedCommit && !skipFreshnessFloor;

  // Resolve the operator-tunable fetch timeout once (guard against non-positive
  // env-config values that would abort the fetch immediately).
  const resolvedTimeoutMs =
    typeof timeoutMs === 'number' && timeoutMs > 0 ? timeoutMs : FETCH_TIMEOUT_MS;

  // Import the trusted SPKI keys once, lazily, and memoize. A PEM that fails to
  // import (an un-provisioned placeholder) is dropped rather than fatal — the
  // remaining trusted keys still verify. If ALL drop, the empty list makes
  // verification fail closed (no trusted key, no load).
  let publicKeysPromise: Promise<ManifestVerifyKey[]> | undefined;
  const getPublicKeys = (): Promise<ManifestVerifyKey[]> =>
    (publicKeysPromise ??= Promise.all(
      trustedPems.map((pem) =>
        importSPKI(pem, 'ES256').then(
          (key) => key,
          () => null,
        ),
      ),
    ).then((keys) => keys.filter((key): key is ManifestVerifyKey => key !== null)));

  // The verified remoteEntry SRI, set by loadRemoteSnapshot, read by
  // createScript. Every hook skips remotes other than abax-ui, so one value
  // (not a per-name map) is correct.
  let expectedIntegrity: string | undefined;

  // The signed per-file integrity map (filename -> "sha384-..."), stashed by
  // loadRemoteSnapshot and read by createScript. It covers remoteEntry.js AND
  // every emitted chunk, so a script the runtime preloads through createScript
  // (an expose's sync asset, not only the remoteEntry) gets ITS OWN hash rather
  // than the remoteEntry's. `remoteEntryName` identifies the trust-anchor script
  // for the pinned-mode SRI guard (defaults to the conventional filename until
  // the verified manifest names it).
  let chunkIntegrity: Record<string, string> | undefined;
  let remoteEntryName = 'remoteEntry.js';

  // Verified-manifest latch: URLs whose signature this plugin has checked. It
  // lets `loadRemoteSnapshot` prove the manifest it is about to trust actually
  // came through the `fetch` verify gate, closing the gap where a runtime that
  // fetched the manifest by another path (hook-shape drift) would otherwise have
  // its provenance/SRI trusted unverified.
  const verifiedManifestUrls = new Set<string>();

  let degradeSignalled = false;
  const signalDegraded = (reason: 'tamper' | 'transient', detail: string): void => {
    if (degradeSignalled) return;
    degradeSignalled = true;
    onDegraded?.(reason, detail);
  };

  return {
    name: 'abax-ui-security-plugin',

    /**
     * Intercept the manifest fetch. Allowlist the URL, fetch bytes + sidecar
     * with transient retry, verify the detached signature, and hand MF2 a
     * Response over the verified bytes. Any signature/allowlist failure is a
     * tamper signal and throws fail-closed.
     */
    async fetch(
      manifestUrl: string,
      _init: unknown,
      remoteInfo?: { name?: string },
      resourceContext?: { resourceType?: string },
    ) {
      if (isOtherRemote(remoteInfo?.name)) return undefined;
      // In 2.6.0 the `fetch` hook fires only for manifest JSON, and its
      // `resourceContext` 4th arg is optional. Skip ONLY when the runtime
      // positively tags a non-manifest resource; when the context is absent we
      // still verify (the URL allowlist + signature gate everything), so a
      // future hook-shape that drops the context cannot bypass verification.
      if (resourceContext?.resourceType && resourceContext.resourceType !== 'manifest') {
        return undefined;
      }

      // Same re-homing as the classic loader. The manifest is fetched here, and
      // its directory is what deriveVersionedPublicPath turns into the base for
      // every chunk, so re-homing at this point carries the whole bundle rather
      // than just this one request. Everything downstream — the allowlist, the
      // signature sidecar, the verify latch and the derived publicPath — uses
      // this re-homed form, and `loadRemoteSnapshot` re-homes its own argument
      // the same way so the latch cannot miss on a spelling difference.
      const entryUrl = rehomeCdnUrlToDocumentOrigin(manifestUrl);
      if (!validateRemoteUrl(entryUrl)) {
        throw new ManifestTamperError(`[abax-ui] manifest URL failed allowlist: ${entryUrl}`);
      }
      const sidecarUrl = `${entryUrl}.jws`;
      if (!validateRemoteUrl(sidecarUrl)) {
        throw new ManifestTamperError(`[abax-ui] signature URL failed allowlist: ${sidecarUrl}`);
      }

      const manifestBytes = await fetchWithRetry(entryUrl, resolvedTimeoutMs);

      let detachedJws: string;
      try {
        detachedJws = new TextDecoder().decode(await fetchWithRetry(sidecarUrl, resolvedTimeoutMs));
      } catch (err) {
        // A missing signature is treated as tamper, NOT transient — an
        // unsigned manifest must never load fail-open.
        throw new ManifestTamperError(
          `[abax-ui] manifest signature (${sidecarUrl}) could not be fetched: ${
            err instanceof Error ? err.message : String(err)
          }`,
        );
      }

      // Verify against the trusted key set — the signature is valid if ANY
      // trusted key verifies it (staging trusts staging + production; production
      // trusts only production). Missing/invalid signature is tamper, never
      // fail-open.
      const trustedKeys = await getPublicKeys();
      const trimmedJws = detachedJws.trim();
      let verified = false;
      let lastVerifyError: unknown;
      for (const key of trustedKeys) {
        try {
          // eslint-disable-next-line no-await-in-loop -- keys tried in order; the first match wins and breaks
          await verifyDetachedManifest(manifestBytes, trimmedJws, key);
          verified = true;
          break;
        } catch (err) {
          lastVerifyError = err;
        }
      }
      if (!verified) {
        throw new ManifestTamperError(
          `[abax-ui] manifest signature verification failed against ${
            trustedKeys.length
          } trusted key(s): ${
            lastVerifyError instanceof Error
              ? lastVerifyError.message
              : trustedKeys.length === 0
              ? 'no importable trusted key'
              : String(lastVerifyError)
          }`,
        );
      }

      // The bytes are authentic — record it so loadRemoteSnapshot can prove the
      // manifest it trusts passed through this gate.
      verifiedManifestUrls.add(entryUrl);

      // The bytes are authentic. Read the (now trusted) manifest to rewrite
      // `publicPath` to the absolute versioned base so this manifest can be
      // served from a STABLE, revalidated URL while its remoteEntry + chunks
      // still resolve to the versioned, immutable path. The IID and derived URL
      // are TRUSTED inputs (signed IID + verified manifest URL) but not covered
      // by the signature, so the derived URL is re-validated against the
      // allowlist / same-origin. (The freshness floor is enforced later, in
      // loadRemoteSnapshot, AFTER the commit pin + remoteEntry SRI validate, so a
      // manifest that fails a later gate never raises the floor.)
      let outBytes = manifestBytes;
      try {
        const manifest = JSON.parse(new TextDecoder().decode(manifestBytes));
        const iid: unknown = manifest?.metaData?.pipelineIid;

        if (typeof iid === 'string' && iid && SAFE_SEGMENT_RE.test(iid)) {
          const publicPath = deriveVersionedPublicPath(entryUrl, iid);
          // publicPath is host-derived, not signed. Bind it to the verified
          // manifest's own origin so it can never point at a different host even
          // within the allowlist (it always does by construction; assert it).
          if (new URL(publicPath).origin !== new URL(entryUrl).origin) {
            throw new ManifestTamperError(
              `[abax-ui] derived publicPath origin differs from the verified manifest: ${publicPath}`,
            );
          }
          if (!validateRemoteUrl(publicPath)) {
            throw new ManifestTamperError(
              `[abax-ui] derived publicPath failed allowlist: ${publicPath}`,
            );
          }
          manifest.metaData.publicPath = publicPath;
          outBytes = new TextEncoder().encode(JSON.stringify(manifest));
        }
      } catch (err) {
        // A ManifestTamperError (allowlist / same-origin) is a real security
        // stop — rethrow. A JSON parse error on verified bytes is a producer
        // bug, not tamper; fall back to the authentic bytes and let MF2 surface
        // any real problem.
        if (err instanceof ManifestTamperError) throw err;
      }

      return new Response(outBytes, {
        status: 200,
        headers: { 'content-type': 'application/json' },
      });
    },

    /**
     * The manifest reaching here is signature-verified. Confirm it came through
     * the verify latch, enforce the commit pin against the trusted provenance,
     * and stash the remoteEntry SRI.
     */
    async loadRemoteSnapshot(args: {
      moduleInfo?: { name?: string };
      manifestUrl?: string;
      manifestJson?: {
        metaData?: {
          provenance?: ManifestProvenance;
          pipelineIid?: unknown;
          chunkIntegrity?: Record<string, string>;
          remoteEntry?: { name?: string };
        };
        name?: string;
      };
    }) {
      if (isOtherRemote(args.moduleInfo?.name)) return args;

      // Defense-in-depth with the fetch gate: if the runtime reached snapshot
      // with a manifest URL this plugin never verified, refuse it rather than
      // trust attacker-controlled provenance/SRI.
      // Look the latch up under the SAME re-homed spelling the fetch hook
      // registered. The runtime hands back whatever entry it was configured
      // with; if that arrived canonical while fetch recorded the re-homed form,
      // an un-normalised lookup would miss and fail a legitimate load closed.
      const snapshotUrl = args.manifestUrl
        ? rehomeCdnUrlToDocumentOrigin(args.manifestUrl)
        : args.manifestUrl;
      if (snapshotUrl && !verifiedManifestUrls.has(snapshotUrl)) {
        throw new ManifestTamperError(
          `[abax-ui] snapshot manifest was not signature-verified: ${snapshotUrl}`,
        );
      }

      const manifest = args.manifestJson;

      // Stash the signed per-file integrity map + the remoteEntry filename before
      // any provenance branching, so createScript can apply the correct per-file
      // SRI regardless of pin posture. The manifest is signature-verified (latch
      // check above), so these values are trusted.
      const rawChunkIntegrity = manifest?.metaData?.chunkIntegrity;
      if (rawChunkIntegrity && typeof rawChunkIntegrity === 'object') {
        chunkIntegrity = rawChunkIntegrity;
      }
      const declaredEntryName = manifest?.metaData?.remoteEntry?.name;
      if (typeof declaredEntryName === 'string' && declaredEntryName) {
        remoteEntryName = declaredEntryName;
      }

      if (!manifest?.metaData?.provenance) {
        // A verified MF2 manifest must carry provenance; its absence after a
        // passing signature means the signed producer did not emit it — treat
        // as a hard misconfig, fail closed.
        if (requireCommitPin || expectedCommit) {
          throw new ManifestTamperError('[abax-ui] signed manifest is missing metaData.provenance');
        }
        return args;
      }
      const provenance = manifest.metaData.provenance;

      const manifestCommit =
        provenance.buildDefinition?.resolvedDependencies?.[0]?.digest?.gitCommit;
      if (expectedCommit) {
        if (manifestCommit !== expectedCommit) {
          throw new ManifestTamperError(
            `[abax-ui] commit pin mismatch: expected ${expectedCommit}, got ${
              manifestCommit ?? '<none>'
            }`,
          );
        }
      } else if (requireCommitPin) {
        throw new ManifestTamperError(
          '[abax-ui] expectedCommit pin is required in this environment but was not set',
        );
      }

      const remoteEntrySha = provenance.subject?.find((s) => s.name === 'remoteEntry.js')?.digest
        ?.sha384;
      if (!remoteEntrySha) {
        // Fail closed in pinned mode: a signed manifest that omits the
        // remoteEntry digest would otherwise load with no SRI, and the JWS does
        // not cover the entry bytes — a CDN rewrite of remoteEntry.js would run
        // unverified. Dev/unpinned may proceed without SRI (documented posture).
        if (pinned) {
          throw new ManifestTamperError(
            '[abax-ui] signed manifest provenance is missing the remoteEntry.js sha384 digest',
          );
        }
        return args;
      }
      const integrity = `sha384-${remoteEntrySha}`;
      if (!INTEGRITY_RE.test(integrity)) {
        throw new ManifestTamperError(
          `[abax-ui] provenance remoteEntry digest is malformed: ${remoteEntrySha}`,
        );
      }
      expectedIntegrity = integrity;

      // Freshness floor — enforced LAST, after the signature (fetch), the commit
      // pin, and the remoteEntry SRI have all validated. A manifest that fails any
      // earlier gate never reaches here, so it can never raise the floor and then
      // wrongly refuse a legitimate older-but-correct manifest as a rollback.
      // Reject an older validly-signed manifest; record the new high-water mark.
      // Bypassed by an explicit commit pin (a deliberate rollback names the
      // commit). Numeric CI IIDs only; local/dev builds (empty IID) skip.
      if (floorEnabled) {
        const iidRaw = manifest.metaData?.pipelineIid;
        if (typeof iidRaw === 'string' && iidRaw) {
          const iidNum = Number.parseInt(iidRaw, 10);
          if (Number.isFinite(iidNum) && iidNum > 0) {
            const floor = readPipelineFloor();
            if (floor != null && iidNum < floor) {
              throw new ManifestTamperError(
                `[abax-ui] manifest is older than the last loaded build (pipelineIid ${iidNum} < floor ${floor}); refusing rollback`,
              );
            }
            recordPipelineFloor(iidNum);
          }
        }
      }

      return args;
    },

    /**
     * Build a hardened <script> for any asset the runtime asks the host to
     * inject — the remoteEntry AND any expose asset MF2 preloads through this
     * same hook. Re-check the final URL against the allowlist, then apply the
     * integrity that matches THIS script, resolved by filename from the signed
     * chunkIntegrity map. Applying the remoteEntry's hash to a preloaded chunk
     * (a single global SRI) is an SRI mismatch the browser rejects, so each
     * script must carry its own hash; a chunk absent from the map loads with no
     * SRI rather than a wrong one. The remoteEntry is the trust anchor (its
     * bytes are not covered by the manifest JWS), so it falls back to the stashed
     * entry SRI and is mandatory in pinned mode.
     */
    createScript(args: {
      url: string;
      remoteInfo?: { name?: string };
    }): HTMLScriptElement | undefined {
      if (isOtherRemote(args.remoteInfo?.name)) return undefined;

      // The MF2 runtime injects chunks through this hook, so it is a
      // script-loading path of its own and needs the same treatment as the
      // other two. Normally a no-op, because these URLs are built from an
      // already-re-homed publicPath, but the runtime can also hand back a URL
      // taken from the manifest verbatim. Idempotent either way.
      const scriptUrl = rehomeCdnUrlToDocumentOrigin(args.url);
      if (!validateRemoteUrl(scriptUrl)) {
        throw new ManifestTamperError(`[abax-ui] script URL failed allowlist: ${scriptUrl}`);
      }
      const filename = filenameFromScriptUrl(scriptUrl);
      const isRemoteEntry = filename !== undefined && filename === remoteEntryName;

      let integrity = filename ? chunkIntegrity?.[filename] : undefined;
      if (integrity && !INTEGRITY_RE.test(integrity)) integrity = undefined;
      // The remoteEntry falls back to the SRI stashed from provenance when the
      // map lookup misses (they carry the same value by construction).
      if (!integrity && isRemoteEntry) integrity = expectedIntegrity;

      if (isRemoteEntry && !integrity && pinned) {
        // Defense-in-depth with loadRemoteSnapshot: never inject the remoteEntry
        // script without SRI in pinned mode, even if the stash path was bypassed.
        throw new ManifestTamperError(
          `[abax-ui] refusing to load remoteEntry without integrity (pinned): ${scriptUrl}`,
        );
      }
      const script = document.createElement('script');
      script.src = scriptUrl;
      if (integrity) script.integrity = integrity;
      script.crossOrigin = 'anonymous';
      script.referrerPolicy = 'no-referrer';
      return script;
    },

    /**
     * The tamper-vs-transient fork. Returning a falsy value makes the runtime
     * re-throw (fail-closed); the host's preload catch then flips
     * `__ABAX_UI_LOAD_FAILED__` and the consumer stub renders the fallback.
     * A tamper error is logged as a security event and NEVER recovered.
     */
    async errorLoadRemote(args: {
      id?: string;
      error?: unknown;
      lifecycle?: string;
      remote?: { name?: string };
    }) {
      if (isOtherRemote(args.remote?.name ?? remoteNameFromRequestId(args.id))) return undefined;

      const err = args.error;
      if (err instanceof ManifestTamperError) {
        signalDegraded('tamper', err.message);
        // eslint-disable-next-line no-console
        console.error('[abax-ui] tamper signal — refusing to recover the remote', err);
        return undefined;
      }
      const detail =
        err instanceof ManifestHttpError
          ? `http ${err.status}`
          : err instanceof Error
          ? err.message
          : String(err);
      signalDegraded('transient', detail);
      // eslint-disable-next-line no-console
      console.warn('[abax-ui] transient load error — degrading', {
        lifecycle: args.lifecycle ?? '?',
        cause: err,
      });
      return undefined;
    },
  };
}

/** @internal test seam. */
// eslint-disable-next-line @typescript-eslint/naming-convention -- matches the repo's __internal test-seam convention (cf. manifest.ts).
export const __internalForTests = {
  verifyDetachedManifest,
  fetchWithRetry,
  INTEGRITY_RE,
  SAFE_SEGMENT_RE,
};
